import { Injectable, inject, signal, untracked } from '@angular/core';

import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { LoggerService } from '@/core/services/logger.service';

import { fotoComoDataUri } from './avatar-responsavel.presenter';

/**
 * As fotos dos responsáveis, por id de usuário.
 *
 * <p>Busca em LOTE, e não uma por avatar: a foto é um LOB no banco, e uma tela com trinta analistas
 * faria trinta requisições — a mesma razão pela qual o quadro pede as dele de uma vez só.</p>
 *
 * <p><b>Falhar aqui não pode derrubar a tela.</b> Sem foto o avatar cai nas iniciais, que é também o
 * caso comum de quem simplesmente não tem retrato cadastrado — o backend só devolve quem tem. Por
 * isso o erro é registrado e engolido.</p>
 */
@Injectable({ providedIn: 'root' })
export class FotosResponsaveisService {
    private readonly api = inject(KanbanServicosService);
    private readonly logger = inject(LoggerService);

    private readonly porUsuario = signal<ReadonlyMap<number, string>>(new Map());
    /**
     * Ids já pedidos, com ou sem foto no retorno. Fora do sinal de propósito: serve para não repetir
     * a chamada, não para desenhar — e quem não tem foto precisa ficar registrado aqui, senão cada
     * nova renderização o pediria de novo.
     */
    private readonly consultados = new Set<number>();

    /** Foto pronta para o `src`, ou `null` quando a pessoa não tem retrato (ou ele ainda não chegou). */
    fotoPorId(idWebUsuario: number | null | undefined): string | null {
        if (idWebUsuario === null || idWebUsuario === undefined) {
            return null;
        }
        return fotoComoDataUri(this.porUsuario().get(idWebUsuario));
    }

    /**
     * Busca as fotos que ainda faltam e MESCLA o resultado.
     *
     * <p>Mesclar, e não substituir: duas telas podem pedir lotes diferentes, e trocar o mapa apagaria
     * as fotos que a outra acabou de trazer.</p>
     */
    carregar(ids: readonly number[]): void {
        // `untracked`: ler o mapa aqui dentro registraria o sinal como dependência de quem chamou —
        // e o `update` logo abaixo dispararia o efeito de novo, em laço.
        const jaTem = untracked(() => this.porUsuario());
        const faltando = [...new Set(ids)].filter((id) => Number.isFinite(id) && !jaTem.has(id) && !this.consultados.has(id));

        if (!faltando.length) {
            return;
        }
        faltando.forEach((id) => this.consultados.add(id));

        this.api.listarFotosResponsaveis(faltando).subscribe({
            next: (fotos) => {
                this.porUsuario.update((atual) => {
                    const mesclado = new Map(atual);
                    (fotos ?? []).forEach((foto) => mesclado.set(foto.idWebUsuario, foto.fotoBase64));
                    return mesclado;
                });
            },
            error: (falha: unknown) => {
                // Sem as fotos o avatar cai nas iniciais e a tela segue: o retrato é reforço, não dado.
                this.logger.error('Falha ao carregar as fotos dos responsáveis (os avatares ficarão com as iniciais):', falha);
                // Liberados para uma nova tentativa: a falha pode ter sido de rede, e quem não tem
                // foto o backend já teria respondido sem ela.
                faltando.forEach((id) => this.consultados.delete(id));
            }
        });
    }
}
